import {
  Box,
  Button,
  Form,
  FormField,
  Icon,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import FileSaver from 'file-saver';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';
import { Redirect } from 'react-router-dom';
import {
  getCategoryOptions,
  getSubcategoryOptions,
  sortOptions,
} from '../../../../utils/ContainerManagement/containerDefinitionsHelper';
import '../style.css';

const CreateIVTAVT = (props: any): ReactElement => {
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const { data } = props;
  const { failedRequests } = data;
  const { categoriesMap } = data;
  const { rooms } = data;
  const initialValues = {
    containerDefinitionId: '',
    type: '',
    category: '',
    subcategory: '',
    room: '',
    roomLocationId: '',
    size: 'N/A',
  };
  const [siteId, setSiteId] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [categoryOptions, setCategoryOptions] = useState<SelectProps.Option[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectProps.Option | null>(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState<SelectProps.Option[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SelectProps.Option | null>();
  const [roomOptions, setRoomOptions] = useState([]);
  const [roomSelectedOption, setRoomSelectedOption] = useState([]);
  const [numberOfBins, setNumberOfBins] = useState(0);
  const [disableAddButton, setDisableAddButton] = useState(true);
  const [binDetails, setBinDetails] = useState([
    {
      id: uuid(),
      siteRoom: '',
      cell: '',
      errors: {
        siteRoom: '',
        cell: '',
      },
    },
  ]);
  const [redirect, setRedirect] = useState('');

  useEffect(() => {
    const loadData = () => {
      setCategoryOptions(getCategoryOptions(categoriesMap));
      setRoomOptions(rooms);
      if (localStorage.getItem('siteId')) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setSiteId(JSON.parse(localStorage.getItem('siteId')!).value);
      }
      setBinDetails([]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesMap]);

  useEffect(() => {
    formValues.containerDefinitionId = selectedSubcategory?.value ?? '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory]);

  useEffect(() => {
    if (subcategoryOptions.length === 1) {
      setSelectedSubcategory(subcategoryOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcategoryOptions]);

  useEffect(() => {
    setSelectedSubcategory(null);
    setSubcategoryOptions(getSubcategoryOptions(categoriesMap, selectedCategory));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryOptions.length === 1) {
      setSelectedCategory(categoryOptions[0]);
    }
  }, [categoryOptions]);

  // Prefills values
  useEffect(() => {
    const loadPrefillData = () => {
      const preFillOption = roomOptions.find((obj: any) => {
        return obj.label === failedRequests.formValues.room;
      });
      if (preFillOption) {
        setRoomSelectedOption(preFillOption);
        _.set(formValues, 'room', failedRequests.formValues.room);
        _.set(formValues, 'roomLocationId', failedRequests.formValues.roomLocationId);
        setBinDetails(failedRequests.binDetailsFlex);
        setNumberOfBins(failedRequests.binDetailsFlex.length);
        setDisableAddButton(false);
        setIsSubmit(false);
      }
    };
    if (failedRequests) {
      loadPrefillData();
    }
  }, [failedRequests, formValues, roomOptions]);

  // Creates dropdown with parameters
  const createDropDown = (option: any, onchange: any, options: any, name: any) => {
    return (
      <Select
        selectedOption={option}
        onChange={({ detail }) => onchange(detail.selectedOption)}
        options={options}
        placeholder="Choose an option"
        selectedAriaLabel="Selected"
        empty="No options"
        className={name}
        name={name}
        disabled={isSubmit}
      />
    );
  };

  // Checks for special characters.
  const checkForSpecialChar = (str: string) => {
    // eslint-disable-next-line no-useless-escape
    const spChars = /[!@#$%^&*()+\=\[\]{};':"\\|,.\/?]+/;
    return spChars.test(str);
  };

  // checks for space
  const checkForSpace = (str: string) => {
    return /\s/.test(str);
  };

  // Validates values
  const checkError = (value: string, required: boolean) => {
    const requiredCheck = 'Required';
    const lengthCheck = `Can't exceed 50 characters`;
    const spacesCheck = `Cannot contain spaces`;
    const specialCharText =
      'Can include only numbers, letters and the following special characters : _ - < >';
    if (required && value === '') {
      return requiredCheck;
    }
    if (value.length > 20) {
      return lengthCheck;
    }
    if (checkForSpecialChar(value)) {
      return specialCharText;
    }
    if (checkForSpace(value)) {
      return spacesCheck;
    }
    return '';
  };

  // validates the room dropdown
  const validateDropdowns = () => {
    const errors = {};
    if (!formValues.room) _.set(errors, ['room'], 'Room is required!');
    if (binDetails.length === 0) _.set(errors, ['bins'], 'No bins added');
    setFormErrors(errors);
    return _.isEmpty(errors);
  };

  const validate = () => {
    if (binDetails.length === 0) {
      return true;
    }
    const errorFields = binDetails.some((item) => checkError(item.cell, true) !== '');

    if (errorFields) {
      // eslint-disable-next-line array-callback-return
      binDetails.map((item, index) => {
        const allPrev = [...binDetails];
        allPrev[index].errors.cell = checkError(binDetails[index].cell, true);
        setBinDetails(allPrev);
      });
    }

    return !errorFields;
  };

  // Handles input change and adds it to the binDetails
  const handleInputChange = (id: string, obj: any, name: string) => {
    const newInputFields = binDetails.map((i) => {
      if (id === i.id) {
        if (name === 'cell') {
          // eslint-disable-next-line no-param-reassign
          i.cell = obj.toUpperCase();
          // eslint-disable-next-line no-param-reassign
          i.errors.cell = checkError(i.cell, true);
        }
      }
      return i;
    });
    setBinDetails(newInputFields);
  };

  // Adds a set of input fields for bin name and cell.
  // Or bin name and number based on the type.
  const handleAddFields = () => {
    _.set(formErrors, ['bins'], '');
    setBinDetails([
      ...binDetails,
      {
        id: uuid(),
        siteRoom: `${siteId}.${JSON.parse(JSON.stringify(roomSelectedOption)).value}`,
        cell: '',
        errors: {
          siteRoom: '',
          cell: '',
        },
      },
    ]);
    const values = [...binDetails];
    setNumberOfBins(values.length + 1);
  };

  // Removes the set of input fields for room site and cell.
  const handleRemoveFields = () => {
    const values = [...binDetails];
    values.splice(values.length - 1, 1);
    setBinDetails(values);
    setNumberOfBins(values.length);
  };

  // Autofills the set of input fields for cell.
  const handleAutoFillFields = () => {
    const firstBin = binDetails[0];
    let incrementValue = Number(firstBin.cell.replace(/^\D+/g, ''));
    const cellValue = firstBin.cell.replace(/\d+$/, '');
    const newBins = binDetails.map((obj, index) => {
      if (index < 1) {
        return obj;
      }
      incrementValue += 1;
      return {
        ...obj,
        siteRoom: firstBin.siteRoom,
        cell:
          cellValue +
          incrementValue.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          }),
      };
    });
    setBinDetails(newBins);
  };

  // Handles room change
  const handleRoomChange = (obj: any) => {
    _.set(formErrors, ['room'], '');
    setBinDetails([]);
    setNumberOfBins(0);
    setFormValues((values) => ({
      ...values,
      room: obj.value,
      roomLocationId: obj.locationId,
    }));
    setRoomSelectedOption(obj);
    setDisableAddButton(false);
  };

  // Handles submit
  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (validate() && validateDropdowns()) {
      setIsSubmit(true);
      props.setHasSubmitted(true);
      props.setFormValuesIVTAVT({
        formValues,
        binDetails: [],
        binDetailsFlex: binDetails,
      });
    }
  };
  // Handles number of bins change
  const handleNumberOfBinsCountChange = (count: any) => {
    const bins: any[] = [];
    if (Number(count) <= 50) {
      for (let i = 0; i < Number(count); i += 1) {
        bins.push({
          id: uuid(),
          siteRoom: `${siteId}.${JSON.parse(JSON.stringify(roomSelectedOption)).value}`,
          cell: '',
          errors: {
            siteRoom: '',
            cell: '',
          },
        });
      }
      setBinDetails(bins);
      setNumberOfBins(bins.length);
    } else {
      setBinDetails([]);
      setNumberOfBins(Number(count));
    }
  };

  const createInputField = (
    placeholder: any,
    value: any,
    onchange: any,
    id: any,
    name: string,
    disabled?: boolean
  ) => {
    if (disabled) {
      return (
        <Input
          placeholder={placeholder}
          id={id}
          className={name}
          value={value}
          name={name}
          disabled
        />
      );
    }
    return (
      <Input
        placeholder={placeholder}
        id={id}
        className={name}
        value={value}
        name={name}
        disabled={isSubmit}
        onChange={({ detail }) => onchange(id, detail.value, name)}
      />
    );
  };

  const downloadCsv = () => {
    const blob = new Blob([`${jsonToCSV(props.csvData)}`], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${props.csvFileName}.csv`);
  };

  // Handles reset button
  const handleResetButton = () => {
    setRoomSelectedOption(roomOptions[-1]);
    setNumberOfBins(0);
    setBinDetails([]);
    setFormErrors({});
    setFormValues(initialValues);
    setDisableAddButton(true);
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button data-test-id="cancelButton" onClick={() => setRedirect('/container')}>
              Cancel
            </Button>
            <Button
              className="submitButton"
              disabled={isSubmit}
              variant="primary"
              onClick={handleSubmit}
            >
              {t('create-bin.title')}
            </Button>
            <Button disabled={!isSubmit} variant="primary" onClick={downloadCsv}>
              Download
            </Button>
          </SpaceBetween>
        }
      >
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xxl">
            <div className="resetButtonStyle">
              <Button
                id="reset"
                formAction="none"
                className="resetButton"
                iconAlign="right"
                disabled={isSubmit}
                onClick={() => handleResetButton()}
              >
                Reset
              </Button>
            </div>
          </SpaceBetween>
        </Box>
        <SpaceBetween direction="vertical" size="l">
          <FormField label={t('create-bin.category-label')}>
            <div className="dropDownStyle">
              {createDropDown(
                selectedCategory,
                setSelectedCategory,
                sortOptions(categoryOptions),
                'category'
              )}
            </div>
          </FormField>
          <FormField label={t('create-bin.subcategory-label')}>
            <div className="dropDownStyle">
              {createDropDown(
                selectedSubcategory,
                setSelectedSubcategory,
                sortOptions(subcategoryOptions),
                'subcategory'
              )}
            </div>
          </FormField>
          <FormField label={t('create-bin.room-label')}>
            <div className="dropDownStyle">
              {createDropDown(
                roomSelectedOption,
                handleRoomChange,
                sortOptions(roomOptions),
                'room'
              )}
              {JSON.parse(JSON.stringify(formErrors)).room && (
                <small className="roomError" style={{ color: 'red' }}>
                  {JSON.parse(JSON.stringify(formErrors)).room}
                </small>
              )}
            </div>
          </FormField>
          <FormField label={t('create-bin.no-of-bins')}>
            <SpaceBetween direction="horizontal" size="xs">
              <div
                style={{
                  width: '50px',
                  overflow: 'hidden',
                }}
              >
                <Input
                  className="numberOfBins"
                  value={numberOfBins.toString()}
                  name="numberOfBins"
                  disabled={isSubmit || disableAddButton}
                  onChange={({ detail }) => handleNumberOfBinsCountChange(detail.value)}
                />
              </div>
              <Button
                className="addButton"
                formAction="none"
                iconAlign="right"
                disabled={disableAddButton || numberOfBins >= 50 || isSubmit}
                onClick={() => handleAddFields()}
              >
                <Icon name="add-plus" />
              </Button>
              <Button
                className="removeButton"
                formAction="none"
                iconAlign="right"
                disabled={numberOfBins === 0 || isSubmit}
                onClick={() => handleRemoveFields()}
              >
                -
              </Button>
              {binDetails.length > 1 && binDetails[0].cell !== '' && (
                <Button
                  className="autofillButton"
                  formAction="none"
                  iconAlign="right"
                  disabled={disableAddButton || numberOfBins === 0 || isSubmit}
                  onClick={() => handleAutoFillFields()}
                >
                  Autofill
                </Button>
              )}
            </SpaceBetween>
            {JSON.parse(JSON.stringify(formErrors)).bins && (
              <small className="binsEmptyError" style={{ color: 'red' }}>
                {JSON.parse(JSON.stringify(formErrors)).bins}
              </small>
            )}
            {numberOfBins > 50 && (
              <small className="binLimitError" style={{ color: 'red' }}>
                Cannot add more than 50 Bins
              </small>
            )}
          </FormField>
          <FormField label={binDetails.length > 0 ? t('create-bin.bin-name') : ''}>
            <SpaceBetween direction="horizontal" size="xs">
              {binDetails.map((inputField) => (
                <div
                  id="inputDiv"
                  key={inputField.id}
                  style={{
                    width: '700px',
                    overflow: 'hidden',
                  }}
                >
                  <SpaceBetween direction="horizontal" size="xs">
                    {/* TODO: add validations for the fields  */}
                    <div className="dropDownStyle">
                      {createInputField(
                        'Bin Name',
                        inputField.siteRoom,
                        handleInputChange,
                        inputField.id,
                        'siteRoom',
                        true
                      )}
                    </div>
                    <div className="dropDownStyle">
                      {createInputField(
                        'Container Name',
                        inputField.cell,
                        handleInputChange,
                        inputField.id,
                        'cell'
                      )}
                      {inputField.errors.cell && (
                        <small
                          className="cellError"
                          style={{
                            color: 'red',
                          }}
                        >
                          {inputField.errors.cell}
                        </small>
                      )}
                    </div>
                  </SpaceBetween>
                </div>
              ))}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Form>
    </form>
  );
};
export default CreateIVTAVT;
